import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import {
  WHATS_NEW_LINK,
  type WhatsNewView,
} from "@/v2-common/featureSurface/featureSurface.constants"
import ToggleGroup from "@/v2-ui/toggleGroup"
import LicenseChangesTable from "@/v2-backoffice-client/license/changes/LicenseChangesTable"
import LicenseContentTable from "@/v2-backoffice-client/license/changes/LicenseContentTable"
import { useFeatureSurfaceChangesQuery } from "@/v2-backoffice-client/license/changes/license.changes.query"
import { countUndecidedFeatures } from "@/v2-backoffice-client/license/changes/license.changes.utils"
import { isLockedContent } from "@/v2-backoffice-client/license/changes/license.content.utils"
import { useFeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.contentCatalog.query"

const VIEWS: readonly { value: WhatsNewView; label: string }[] = [
  { value: WHATS_NEW_LINK.viewCapabilities, label: "Capabilities" },
  { value: WHATS_NEW_LINK.viewContent, label: "Content" },
]

function isWhatsNewView(value: string | null): value is WhatsNewView {
  return VIEWS.some((view) => view.value === value)
}

/** The view in the URL (WHATS_NEW_LINK.viewParam), capabilities by default. */
function useFeatureAccessView() {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get(WHATS_NEW_LINK.viewParam)
  const view: WhatsNewView = isWhatsNewView(raw)
    ? raw
    : WHATS_NEW_LINK.viewCapabilities

  function setView(next: WhatsNewView) {
    setSearchParams(
      (prev) => {
        if (next === WHATS_NEW_LINK.viewCapabilities) {
          prev.delete(WHATS_NEW_LINK.viewParam)
        } else {
          prev.set(WHATS_NEW_LINK.viewParam, next)
        }
        return prev
      },
      { replace: true },
    )
  }

  return [view, setView] as const
}

// Feature access: the one board where "who has this" is decided, in two
// views over the two kinds of thing a package can carry. Capabilities are
// the console surfaces the change feed found in Fusion's code; content is
// the map's base maps and layers. Each view counts what asks for attention
// on its switch (capabilities waiting on a decision, content that is
// package-only), from the reads the tables already run. The board was
// called "What's new" until it outgrew the name (it lists every feature,
// not the recent ones); the URL keys keep the old word so deep links from
// the console and the layer sheets survive.
function LicenseFeatureAccess() {
  const [view, setView] = useFeatureAccessView()

  const { data: changesData } = useFeatureSurfaceChangesQuery()
  const { data: contentData } = useFeatureContentCatalogQuery()
  const undecidedCount = useMemo(
    () => countUndecidedFeatures(changesData?.getFeatureSurfaceChanges ?? []),
    [changesData?.getFeatureSurfaceChanges],
  )
  const lockedContentCount = useMemo(
    () =>
      (contentData?.getFeatureContentCatalog ?? []).filter(isLockedContent)
        .length,
    [contentData?.getFeatureContentCatalog],
  )
  const counts: Record<WhatsNewView, number> = {
    [WHATS_NEW_LINK.viewCapabilities]: undecidedCount,
    [WHATS_NEW_LINK.viewContent]: lockedContentCount,
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <ToggleGroup
        type="single"
        size="xs"
        variant="outline"
        value={view}
        className="self-start"
        aria-label="What to decide on"
        onValueChange={(next) => {
          if (isWhatsNewView(next)) setView(next)
        }}
      >
        {VIEWS.map((item) => (
          <ToggleGroup.Item
            key={item.value}
            value={item.value}
            className="gap-1.5"
            data-feature-code="UI_BackofficeWhatsNew_View_Switch"
          >
            {item.label}
            {counts[item.value] > 0 && (
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {counts[item.value]}
              </span>
            )}
          </ToggleGroup.Item>
        ))}
      </ToggleGroup>

      {view === WHATS_NEW_LINK.viewContent ? (
        <LicenseContentTable />
      ) : (
        <LicenseChangesTable />
      )}
    </div>
  )
}

export default LicenseFeatureAccess
